import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import { SkillService, type Skill, type UpdateSkillRequest } from '../../../services/skillService'
import { InlinePopover, ChipRow } from '../../tasks/inline/InlinePopover'
import { ParentPicker, type ParentType } from '../../hierarchy/ParentPicker'
import { CADENCE_OPTIONS, LEVEL_OPTIONS, SKILL_PRIORITY_OPTIONS } from '../../../utils/skillOptions'
import { levelWords, cadenceWords, capabilityOf } from '../../../utils/skillWords'
import { Timestamp } from '../../ui/Timestamp'

/**
 * A skill's facts, each edited where it is shown (Skills UX pass 2026-09-06,
 * the task page's philosophy): the title, why it matters, where you are and
 * where you want to get, by when, how often you practise, what it belongs
 * to, priority. With these the edit mode has nothing left to offer.
 */
interface EditorProps {
  skill: Skill
  onChanged: (skill: Skill) => void
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
  openRequest?: number
}

async function write(skill: Skill, payload: UpdateSkillRequest, onChanged: (s: Skill) => void) {
  const svc = new SkillService()
  await svc.updateSkill(String(skill.id), payload)
  onChanged(await svc.getSkill(String(skill.id)))
}

const inputClass = 'w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'

// ── Title ──────────────────────────────────────────────────────────────────

export function SkillTitleEditor({ skill, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(skill.title || '')
  const save = async () => {
    const text = value.trim()
    if (!text) throw new Error('A skill needs a name.')
    if (text === (skill.title || '').trim()) return
    await write(skill, { title: text }, onChanged)
  }
  return (
    <InlinePopover label="Title" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-title" width="w-96" trigger={trigger ?? skill.title}>
      <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Title" autoFocus className={inputClass} placeholder="Name the skill as the thing you can do — Public speaking, React, Saying no" />
    </InlinePopover>
  )
}

// ── Why it matters ─────────────────────────────────────────────────────────

export function SkillWhyEditor({ skill, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [text, setText] = useState('')
  const reset = () => setText(skill.description || '')
  const save = async () => { await write(skill, { description: text.trim() || null }, onChanged) }
  return (
    <InlinePopover label="Why it matters" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-description" width="w-96"
                   trigger={trigger ?? (skill.description || <><Plus className="h-3 w-3" /> why it matters</>)}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} aria-label="Why it matters" autoFocus placeholder="What having this skill would change — one line is plenty" className={`${inputClass} resize-y`} />
      {skill.description && <button type="button" onClick={() => setText('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Clear it</button>}
    </InlinePopover>
  )
}

// ── The ladder: where you are, where you want to get ───────────────────────

export function SkillLevelsEditor({ skill, onChanged, trigger, variant = 'dotted', className, openRequest }: EditorProps) {
  const [cur, setCur] = useState('')
  const [tgt, setTgt] = useState('')
  const reset = () => { setCur(skill.current_level || ''); setTgt(skill.target_level || '') }
  const save = async () => {
    if (!cur && !tgt) throw new Error('Pick where you are, where you want to get, or both.')
    await write(skill, { current_level: cur || null, target_level: tgt || null }, onChanged)
  }
  const chips = LEVEL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))
  return (
    <InlinePopover label="Where you are and where you want to get" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-levels" width="w-96" openRequest={openRequest}
                   trigger={trigger ?? (levelWords(skill) || <><Plus className="h-3 w-3" /> where you are</>)}>
      <div>
        <div className="mb-1 text-app-micro text-muted">Where you are now</div>
        <ChipRow label="Where you are now" options={chips} value={cur} onChange={setCur} />
        {cur && <p className="mt-1 text-app-micro text-secondary">{capabilityOf(cur)}</p>}
      </div>
      <div>
        <div className="mb-1 text-app-micro text-muted">Where you want to get</div>
        <ChipRow label="Where you want to get" options={chips} value={tgt} onChange={setTgt} />
        {tgt && <p className="mt-1 text-app-micro text-secondary">{capabilityOf(tgt)}</p>}
      </div>
    </InlinePopover>
  )
}

// ── Get there by ───────────────────────────────────────────────────────────

export function SkillTargetDateEditor({ skill, onChanged, trigger, variant = 'dotted', className, openRequest }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue((skill.target_date || '').slice(0, 10))
  const save = async () => { await write(skill, { target_date: value || null }, onChanged) }
  return (
    <InlinePopover label="Get there by" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-target-date" openRequest={openRequest}
                   trigger={trigger ?? (skill.target_date ? <>by <Timestamp value={`${skill.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : <><Plus className="h-3 w-3" /> by when</>)}>
      <input type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Get there by" className={inputClass} />
      <p className="text-app-micro text-muted">A rough date is fine. When it passes, I will ask how it is going — nothing more.</p>
      {skill.target_date && <button type="button" onClick={() => setValue('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">No date</button>}
    </InlinePopover>
  )
}

// ── Practise how often ─────────────────────────────────────────────────────

export function SkillCadenceEditor({ skill, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const [own, setOwn] = useState('')
  const reset = () => {
    const v = skill.practice_cadence || ''
    const known = CADENCE_OPTIONS.some((o) => o.value === v)
    setValue(known ? v : ''); setOwn(known ? '' : v)
  }
  const save = async () => { await write(skill, { practice_cadence: (own.trim() || value) || null }, onChanged) }
  return (
    <InlinePopover label="Practise how often" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-cadence" width="w-96"
                   trigger={trigger ?? (cadenceWords(skill.practice_cadence) || <><Plus className="h-3 w-3" /> how often</>)}>
      <ChipRow label="How often" options={CADENCE_OPTIONS} value={value} onChange={(v) => { setValue(v); setOwn('') }} />
      <input type="text" value={own} onChange={(e) => { setOwn(e.target.value); setValue('') }} placeholder="Or in your words — Tuesdays and Thursdays" aria-label="In your words" className={inputClass} />
    </InlinePopover>
  )
}

// ── Priority ───────────────────────────────────────────────────────────────

export function SkillPriorityEditor({ skill, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(skill.priority || 'medium')
  const save = async () => { await write(skill, { priority: (value || 'medium') as UpdateSkillRequest['priority'] }, onChanged) }
  const current = SKILL_PRIORITY_OPTIONS.find((o) => o.value === skill.priority)?.label || 'Normal'
  return (
    <InlinePopover label="Priority" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-priority" trigger={trigger ?? current}>
      <ChipRow label="Priority" options={SKILL_PRIORITY_OPTIONS} value={value} onChange={setValue} />
    </InlinePopover>
  )
}

// ── Belongs to ─────────────────────────────────────────────────────────────

export function SkillParentEditor({ skill, onChanged, trigger, variant = 'icon', className }: EditorProps) {
  const [parentType, setParentType] = useState<ParentType | ''>('')
  const [parentId, setParentId] = useState<number | string | null>(null)
  const reset = () => { setParentType(skill.parent_goal_id ? 'goal' : ''); setParentId(skill.parent_goal_id ?? null) }
  const save = async () => { await write(skill, { parent_goal_id: parentType === 'goal' && parentId ? Number(parentId) : null }, onChanged) }
  return (
    <InlinePopover label="Belongs to" variant={variant} className={className} onOpen={reset} onSave={save} testId="skill-edit-parent" width="w-96"
                   trigger={trigger ?? (skill.parent?.title || <><Plus className="h-3 w-3" /> belongs to</>)}>
      <ParentPicker allowedTypes={['goal']} parentType={parentType} parentId={parentId} onChange={(f) => { setParentType((f.parent_type || '') as ParentType | ''); setParentId(f.parent_id || null) }} />
    </InlinePopover>
  )
}

// ── "Add" row: the facts not yet set ───────────────────────────────────────

export function SkillAddRow({ skill, onChanged }: { skill: Skill; onChanged: (skill: Skill) => void }) {
  // Related things sit together: the path (levels, by when, how often), then
  // why it matters, then where it belongs (goal, priority).
  const path: React.ReactNode[] = []
  if (!skill.current_level && !skill.target_level) path.push(<SkillLevelsEditor key="levels" skill={skill} onChanged={onChanged} variant="chip" />)
  if (!skill.target_date) path.push(<SkillTargetDateEditor key="by" skill={skill} onChanged={onChanged} variant="chip" />)
  if (!skill.practice_cadence) path.push(<SkillCadenceEditor key="cadence" skill={skill} onChanged={onChanged} variant="chip" />)
  const carry: React.ReactNode[] = []
  if (!skill.description) carry.push(<SkillWhyEditor key="why" skill={skill} onChanged={onChanged} variant="chip" />)
  const belong: React.ReactNode[] = []
  if (!skill.parent?.title && !skill.parent_goal_id) belong.push(<SkillParentEditor key="parent" skill={skill} onChanged={onChanged} variant="chip" />)
  if (skill.priority !== 'high') belong.push(<SkillPriorityEditor key="priority" skill={skill} onChanged={onChanged} variant="chip" trigger={<>priority · {SKILL_PRIORITY_OPTIONS.find((o) => o.value === skill.priority)?.label || 'Normal'}</>} />)
  const groups = [path, carry, belong].filter((g) => g.length > 0)
  if (groups.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="skill-add-details">
      <span className="text-app-micro text-muted">Add</span>
      {groups.map((g, i) => <span key={i} className="flex flex-wrap items-center gap-1.5" data-testid="skill-add-group">{g}</span>)}
    </div>
  )
}
